import React, {
  useEffect,
  useState,
  useRef,
  MouseEvent,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from "../../../components/ui/alert";
import { LinkingData } from "./PdfViewer";
import CSVViewer from "./CsvViewer";
import TextViewer from "./TextViewer";
import MonacoTextViewer from "./MonacoTextViewer";
import {
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize,
  SquareDashedMousePointer,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { pdfDocument, VideoDocument } from "../../../models/extraction";
import { z } from "zod";
import { FileType } from "../../../models/audit/auditSession";
import { isTextFileExtension } from "../../../lib/fileTypes";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";

// Lazy load PDF viewer to reduce initial bundle size
const PDFViewerWithHighlight = lazy(() =>
  import("./PdfViewer").then((module) => ({ default: module.default })),
);

// Lazy load Excel viewer to reduce initial bundle size
const ExcelViewer = lazy(() =>
  import("./ExcelViewer").then((module) => ({
    default: module.default,
  })),
);

// Lazy load Video viewer to reduce initial bundle size
const VideoViewer = lazy(() =>
  import("./VideoViewer").then((module) => ({
    default: module.default,
  })),
);

export type BoundingBox = [
  [number, number],
  [number, number],
  [number, number],
  [number, number],
];

export type ViewerBoundingBox = {
  boundingBox: BoundingBox;
  id?: string;
  pageNumber?: number;
  pageWidth?: number;
  pageHeight?: number;
  // Tickmark callout content (PDF tickmark view): code + citation summary, and
  // the per-L2-step colour for the callout, leader line + dot.
  label?: string;
  note?: string;
  color?: string;
};

// Utility function to convert coordinate array to x, y, width, height format
const convertBoundingBoxToRect = (boundingBox: BoundingBox) => {
  const xCoords = boundingBox.map(([x]) => x);
  const yCoords = boundingBox.map(([, y]) => y);

  const minX = Math.min(...xCoords);
  const maxX = Math.max(...xCoords);
  const minY = Math.min(...yCoords);
  const maxY = Math.max(...yCoords);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

export type FileViewerProps = {
  signedUrl: string;
  fileType: FileType;
  fileName?: string;
  initialPage?: number;
  // Prefer boundingBoxes with per-box page metadata; boundingBox is accepted for backward compatibility
  boundingBoxes?: ViewerBoundingBox[];
  boundingBox?: BoundingBox;
  elementId?: string;
  allowLinking?: boolean;
  allowDeleting?: boolean;
  selectedBoundingBoxIndex?: number;
  selectedBoundingBoxId?: string;
  useMonacoForText?: boolean;
  /**
   * The original width of the page (in the same coordinate space as the bounding box).
   * This is required for FileViewer to internally scale the bounding box to the rendered
   * canvas dimensions. If omitted, the bounding box will be rendered without additional
   * scaling which may result in mis-alignment.
   */
  pageWidth?: number;

  /**
   * The original height of the page (in the same coordinate space as the bounding box).
   * This is required for FileViewer to internally scale the bounding box to the rendered
   * canvas dimensions. If omitted, the bounding box will be rendered without additional
   * scaling which may result in mis-alignment.
   */
  pageHeight?: number;
  defaultSearchValue?: string;
  documentExtraction?: z.infer<typeof pdfDocument>;
  videoDocument?: VideoDocument;
  videoDocumentError?: boolean;
  initialTimestamp?: number;
  /** Render SOX-style tickmark callouts + leader lines over PDFs (uses each
   * bounding box's `label`/`note`). */
  showTickmarks?: boolean;
  onLinkingComplete?: (linkingData: LinkingData) => void;
  onDelete?: (payload: { id?: string; index: number }) => void;
  onSelect?: (payload: { id?: string; index: number }) => void;
};

type ImageViewerProps = {
  signedUrl: string;
  boundingBox?: BoundingBox;
  alt?: string;
  allowLinking?: boolean;
  pageWidth?: number;
  pageHeight?: number;
  setCanvasHeight?: (height: number) => void;
  setCanvasWidth?: (width: number) => void;
  onLinkingComplete?: (linkingData: LinkingData) => void;
};

const ImageViewer: React.FC<ImageViewerProps> = ({
  signedUrl,
  boundingBox,
  alt,
  allowLinking = false,
  pageWidth,
  pageHeight,
  setCanvasHeight,
  setCanvasWidth,
  onLinkingComplete,
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Linking (annotation drawing) state
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [linkingBox, setLinkingBox] = useState<{
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
  } | null>(null);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.3, 5));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.3, 0.1));
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale((prev) => Math.max(0.5, Math.min(prev + delta, 3)));
    }
  };

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (allowLinking && isEditingMode && imageContainerRef.current) {
        // Start drawing a linking box
        const rect = imageContainerRef.current.getBoundingClientRect();
        const clientX = e.clientX - rect.left;
        const clientY = e.clientY - rect.top;
        setIsLinking(true);
        setLinkingBox({
          startX: clientX,
          startY: clientY,
          currentX: clientX,
          currentY: clientY,
        });
        e.preventDefault();
      } else if (scale > 1) {
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    },
    [allowLinking, isEditingMode, scale],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isLinking && linkingBox && imageContainerRef.current) {
        const rect = imageContainerRef.current.getBoundingClientRect();
        const clientX = e.clientX - rect.left;
        const clientY = e.clientY - rect.top;
        setLinkingBox({
          ...linkingBox,
          currentX: clientX,
          currentY: clientY,
        });
      } else if (isDragging) {
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    },
    [isDragging, dragStart, isLinking, linkingBox],
  );

  const handleMouseUp = useCallback(() => {
    if (isLinking && linkingBox && imageRef.current) {
      // Complete the linking
      const left = Math.min(linkingBox.startX, linkingBox.currentX);
      const top = Math.min(linkingBox.startY, linkingBox.currentY);
      const width = Math.abs(linkingBox.currentX - linkingBox.startX);
      const height = Math.abs(linkingBox.currentY - linkingBox.startY);

      // Minimum size threshold
      const MIN_SNIP_SIZE = 5;
      if (width >= MIN_SNIP_SIZE && height >= MIN_SNIP_SIZE) {
        // Get actual displayed image dimensions
        const displayedWidth = imageRef.current.clientWidth;
        const displayedHeight = imageRef.current.clientHeight;

        // Convert canvas coordinates to original image coordinates
        // If pageWidth/pageHeight are provided, use those as the "original" dimensions
        // Otherwise, use the natural image dimensions
        const originalWidth =
          pageWidth || imageRef.current.naturalWidth || displayedWidth;
        const originalHeight =
          pageHeight || imageRef.current.naturalHeight || displayedHeight;

        const scaleX = originalWidth / displayedWidth;
        const scaleY = originalHeight / displayedHeight;

        const pdfX = left * scaleX;
        const pdfY = top * scaleY;
        const pdfWidth = width * scaleX;
        const pdfHeight = height * scaleY;

        const boundingBoxCoords: BoundingBox = [
          [pdfX, pdfY], // top-left
          [pdfX + pdfWidth, pdfY], // top-right
          [pdfX + pdfWidth, pdfY + pdfHeight], // bottom-right
          [pdfX, pdfY + pdfHeight], // bottom-left
        ];

        onLinkingComplete?.({
          pageNumber: 1, // Images are single-page
          boundingBox: boundingBoxCoords,
          value: "",
          includeValue: false,
        });
      }

      setIsLinking(false);
      setLinkingBox(null);
    } else if (isDragging) {
      setIsDragging(false);
    }
  }, [
    isLinking,
    linkingBox,
    isDragging,
    pageWidth,
    pageHeight,
    onLinkingComplete,
  ]);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
    if (isLinking) {
      setIsLinking(false);
      setLinkingBox(null);
    }
  }, [isLinking]);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }

    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Report canvas (image) size back to the parent so that it can calculate
  // the appropriate bounding-box scaling. We do this both on initial load and
  // whenever the container gets resized.
  useEffect(() => {
    if (!containerRef.current || !imageRef.current) return;

    const reportSize = () => {
      if (imageRef.current) {
        const { clientHeight, clientWidth } = imageRef.current;
        setCanvasHeight?.(clientHeight);
        setCanvasWidth?.(clientWidth);
      }
    };

    // Initial report
    reportSize();

    // Observe future resizes
    const resizeObserver = new ResizeObserver(() => reportSize());
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [setCanvasHeight, setCanvasWidth]);

  // Calculate linking box style
  const linkingBoxStyle = useMemo(() => {
    if (!linkingBox) return null;

    const left = Math.min(linkingBox.startX, linkingBox.currentX);
    const top = Math.min(linkingBox.startY, linkingBox.currentY);
    const width = Math.abs(linkingBox.currentX - linkingBox.startX);
    const height = Math.abs(linkingBox.currentY - linkingBox.startY);

    return {
      position: "absolute" as const,
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      height: `${height}px`,
      border: "2px dashed #F94703",
      backgroundColor: "rgba(249, 71, 3, 0.1)",
      pointerEvents: "none" as const,
      zIndex: 20,
    };
  }, [linkingBox]);

  // Determine cursor based on mode
  const getCursor = () => {
    if (allowLinking && isEditingMode) return "crosshair";
    if (isDragging) return "grabbing";
    if (scale > 1) return "grab";
    return "default";
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex flex-col relative bg-stone-50 overflow-hidden"
      onWheel={handleWheel}
    >
      <div className="mx-auto z-20 flex gap-2 p-2 items-center">
        <Button
          variant="outline"
          size="icon"
          className="bg-white hover:bg-stone-100 cursor-pointer"
          onClick={handleZoomIn}
          title="Zoom In"
        >
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="bg-white hover:bg-stone-100 cursor-pointer"
          onClick={handleZoomOut}
          title="Zoom Out"
        >
          <ZoomOut className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="bg-white hover:bg-stone-100 cursor-pointer"
          onClick={handleReset}
          title="Reset View"
        >
          <RotateCw className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="bg-white hover:bg-stone-100 cursor-pointer"
          onClick={toggleFullscreen}
          title="Toggle Fullscreen"
        >
          <Maximize className="h-4 w-4" />
        </Button>
        <div>{Math.round(scale * 100)}%</div>
        {allowLinking && (
          <Button
            variant="outline"
            onClick={() => setIsEditingMode(!isEditingMode)}
            className={`flex items-center gap-2 ml-2 ${
              isEditingMode
                ? "bg-[#F94703] text-on-brand hover:bg-[#F94703] hover:text-on-brand"
                : ""
            }`}
          >
            <SquareDashedMousePointer className="h-4 w-4" />
            Annotate
          </Button>
        )}
      </div>

      <div
        className="w-full h-full flex items-center justify-center overflow-hidden"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{
          cursor: getCursor(),
        }}
      >
        <div
          ref={imageContainerRef}
          className="relative"
          style={{
            transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
            transformOrigin: "center",
            transition: isDragging || isLinking ? "none" : "transform 100ms",
          }}
        >
          <img
            ref={imageRef}
            src={signedUrl}
            alt={alt || "Document preview"}
            className="max-h-full max-w-full object-contain"
            draggable="false"
            onLoad={(e) => {
              setCanvasHeight?.(e.currentTarget.clientHeight);
              setCanvasWidth?.(e.currentTarget.clientWidth);
            }}
          />
          {boundingBox &&
            (() => {
              const rect = convertBoundingBoxToRect(boundingBox);
              return (
                <div
                  className="absolute pointer-events-none border-1 border-blue-500 border-dashed bg-blue-500/10 z-10"
                  style={{
                    top: rect.y,
                    left: rect.x,
                    width: rect.width,
                    height: rect.height,
                  }}
                />
              );
            })()}
          {/* Linking box visualization */}
          {isLinking && linkingBox && linkingBoxStyle && (
            <div style={linkingBoxStyle} />
          )}
        </div>
      </div>
    </div>
  );
};

export const FileViewer: React.FC<FileViewerProps> = ({
  signedUrl,
  fileType,
  fileName,
  initialPage,
  boundingBoxes,
  boundingBox,
  elementId,
  pageWidth,
  pageHeight,
  defaultSearchValue,
  documentExtraction,
  videoDocument,
  videoDocumentError,
  initialTimestamp,
  allowLinking,
  allowDeleting,
  showTickmarks,
  selectedBoundingBoxIndex,
  selectedBoundingBoxId,
  useMonacoForText = true,
  onLinkingComplete,
  onDelete,
  onSelect,
}) => {
  // Track the current page internally
  const [currentPage, setCurrentPage] = useState(initialPage);

  // Track rendered canvas size reported by the underlying viewer
  const [canvasWidth, setCanvasWidth] = useState<number>(0);
  const [canvasHeight, setCanvasHeight] = useState<number>(0);

  const effectiveBoundingBoxes: ViewerBoundingBox[] | undefined =
    useMemo(() => {
      if (boundingBoxes && boundingBoxes.length > 0) return boundingBoxes;
      if (boundingBox)
        return [
          {
            boundingBox,
            pageNumber: initialPage,
            pageWidth,
            pageHeight,
          },
        ];
      return undefined;
    }, [boundingBoxes, boundingBox, initialPage, pageWidth, pageHeight]);

  // Update internal state when initialPage changes
  useEffect(() => {
    if (initialPage !== undefined && initialPage !== currentPage) {
      setCurrentPage(initialPage);
    }
  }, [currentPage, initialPage]);

  const renderViewer = () => {
    switch (fileType?.toLowerCase()) {
      case "pdf":
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner />
              </div>
            }
          >
            <PDFViewerWithHighlight
              key={`${signedUrl}-${currentPage}`}
              signedUrl={signedUrl}
              initialPage={currentPage}
              boundingBoxes={
                effectiveBoundingBoxes
                  ? effectiveBoundingBoxes.map((item) => ({
                      id: item.id,
                      ...convertBoundingBoxToRect(item.boundingBox),
                      pageNumber: item.pageNumber,
                      pageWidth: item.pageWidth,
                      pageHeight: item.pageHeight,
                      label: item.label,
                      note: item.note,
                      color: item.color,
                    }))
                  : undefined
              }
              showTickmarks={showTickmarks}
              selectedBoundingBoxIndex={selectedBoundingBoxIndex}
              selectedBoundingBoxId={selectedBoundingBoxId}
              setCanvasHeight={(height) => {
                setCanvasHeight(height);
              }}
              setCanvasWidth={(width) => {
                setCanvasWidth(width);
              }}
              documentExtraction={documentExtraction}
              allowLinking={allowLinking}
              allowDeleting={allowDeleting}
              onLinkingComplete={onLinkingComplete}
              onDelete={onDelete}
              onSelect={onSelect}
            />
          </Suspense>
        );
      case "csv":
        return (
          <CSVViewer
            elementId={elementId}
            url={signedUrl}
            defaultSearchValue={elementId ? undefined : defaultSearchValue}
          />
        );
      // .xlsm (macro-enabled) is the same OOXML container as .xlsx and renders
      // identically in ExcelViewer; macros simply don't execute in a viewer.
      case "xlsm":
      case "xlsx":
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner />
              </div>
            }
          >
            <ExcelViewer
              fileName={fileName}
              url={signedUrl}
              elementId={elementId}
            />
          </Suspense>
        );
      case "jpeg":
      case "png":
      case "webp":
        return (
          <ImageViewer
            signedUrl={signedUrl}
            boundingBox={(() => {
              const first = effectiveBoundingBoxes?.[0];
              if (!first) return undefined;
              const scaleX = canvasWidth / (first.pageWidth ?? pageWidth ?? 1);
              const scaleY =
                canvasHeight / (first.pageHeight ?? pageHeight ?? 1);
              const bb = first.boundingBox;
              return [
                [bb[0][0] * scaleX, bb[0][1] * scaleY],
                [bb[1][0] * scaleX, bb[1][1] * scaleY],
                [bb[2][0] * scaleX, bb[2][1] * scaleY],
                [bb[3][0] * scaleX, bb[3][1] * scaleY],
              ];
            })()}
            alt={fileName}
            allowLinking={allowLinking}
            pageWidth={pageWidth}
            pageHeight={pageHeight}
            setCanvasHeight={setCanvasHeight}
            setCanvasWidth={setCanvasWidth}
            onLinkingComplete={onLinkingComplete}
          />
        );
      case "mp4":
      case "mov":
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner />
              </div>
            }
          >
            <VideoViewer
              signedUrl={signedUrl}
              fileName={fileName}
              videoDocument={videoDocument}
              videoDocumentError={videoDocumentError}
              initialTimestamp={initialTimestamp}
            />
          </Suspense>
        );
      default:
        // Check if it's a text file extension
        if (fileType && isTextFileExtension(fileType.toLowerCase())) {
          return useMonacoForText ? (
            <MonacoTextViewer
              signedUrl={signedUrl}
              fileName={fileName}
              elementId={elementId}
            />
          ) : (
            <TextViewer
              signedUrl={signedUrl}
              fileName={fileName}
              elementId={elementId}
            />
          );
        }
        return (
          <Alert variant="destructive">
            <AlertTitle>Unsupported File Type</AlertTitle>
            <AlertDescription>
              The file type {fileType} is not supported for viewing.
            </AlertDescription>
          </Alert>
        );
    }
  };

  return renderViewer();
};

export default FileViewer;
